/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */
import { cssProps } from '@oracle/oraclejet-cssprops';
import { classNames } from '../utils/UNSAFE_classNames';
import { Keys, containsKey } from '../utils/UNSAFE_keys';
import { useSelection } from '../hooks/PRIVATE_useSelection';
import { SelectionDetail } from '../UNSAFE_Collection';
import { useTabbableMode } from '../hooks/UNSAFE_useTabbableMode';
import { CheckboxOffIcon, CheckboxOnIcon } from '../UNSAFE_ThemedIcons';
import { Flex } from '../UNSAFE_Flex';
import { useTranslationBundle } from '../hooks/UNSAFE_useTranslationBundle';
import type { BundleType } from '../resources/nls/bundle';

const styles = {
  base: cssProps`${{
    position: 'relative',
    '&:active': {
      transform: 'var(--oj-c-PRIVATE-DO-NOT-USE-radio-checkbox-input-transform-active)',
      transitionProperty: 'transform',
      transitionDuration: 'var(--oj-c-PRIVATE-DO-NOT-USE-animation-duration-xshort)',
      transitionTimingFunction: 'var(--oj-c-PRIVATE-DO-NOT-USE-animation-ease-in-out)'
    }
  }}`,
  container: cssProps`${{
    display: 'inline-block'
  }}`
};

const checkboxStyles = {
  base: cssProps`${{
    fontSize: 'var(--oj-c-PRIVATE-DO-NOT-USE-radio-checkbox-input-size)',
    lineHeight: 0
  }}`,
  unselected: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-radio-checkbox-unselected)'
  }}`,
  selected: cssProps`${{
    color: 'var(--oj-c-PRIVATE-DO-NOT-USE-radio-checkbox-selected)'
  }}`
};

/**
 * Props for the Selector Component
 */
export type Props<K> = {
  /**
   * The key associated with the Selector.
   */
  rowKey: K;
  /**
   * The selected keys.
   */
  selectedKeys: Keys<K>;
  /**
   * aria-label for this selector
   */
  accessibleLabel?: string;
  /**
   * Callback function to invoke when the selected keys has changed when
   * the checkbox is toggled.
   */
  onChange?: (detail: SelectionDetail<K>) => void;
};

// internal component to use in place of the real Checkbox
function Checkbox(props: {
  checked: boolean;
  accessibleLabel?: string;
  onClick?: (event: MouseEvent) => void;
}) {
  const { isTabbable } = useTabbableMode();

  const classes = classNames([
    checkboxStyles.base,
    props.checked ? checkboxStyles.selected : checkboxStyles.unselected
  ]);
  const translations = useTranslationBundle<BundleType>('@oracle/oraclejet-preact');
  const ariaLabel = props.checked
    ? translations.selector_selected()
    : translations.selector_unselected();
  return (
    <div
      class={classes}
      tabIndex={isTabbable ? 0 : -1}
      type="checkbox"
      checked={props.checked}
      aria-label={props.accessibleLabel || ariaLabel}
      onClick={props.onClick}>
      {props.checked ? <CheckboxOnIcon /> : <CheckboxOffIcon />}
    </div>
  );
}

// click event handler used by Selector
const handleClick = (event: Event) => {
  // we don't want container component like ListView to process it
  event.stopPropagation();
};

// update selectionProps onClick event to call event.stopPropagation()
const updateSelectionProps = (selectionProps: any) => {
  // click event handler return from useSelection
  const onClick = selectionProps.onClick;
  if (onClick) {
    // click event handler used by Selector
    selectionProps.onClick = (event: MouseEvent) => {
      onClick(event);
      handleClick(event);
    };
    return selectionProps;
  }
  return { onClick: handleClick };
};

/**
 * The Selector component is used to enable multi-selection in Collection components.
 */
export function Selector<K>({ accessibleLabel, rowKey, selectedKeys, onChange }: Props<K>) {
  const { selectionProps } = useSelection(
    () => rowKey,
    selectedKeys,
    'multiple',
    false,
    'toggle',
    onChange
  );

  // TODO: replace internal with Preact checkbox component once it is available
  return (
    <div class={styles.container} {...updateSelectionProps(selectionProps)}>
      <div class={styles.base}>
        <Flex align="center" justify="center" width="11x" height="11x">
          <Checkbox checked={containsKey(selectedKeys, rowKey)} accessibleLabel={accessibleLabel} />
        </Flex>
      </div>
    </div>
  );
}
